import { useEffect, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { BasicView } from "components/views/SavedViewsMenu";
import { useUpdatePinnedView as useUpdatePinnedViewOld } from "lib/hooks/api/notices/views/useUpdatePinnedView";
import { useUpdatePinnedView } from "lib/hooks/api/pinned_views/useUpdatePinnedView";
import PinnedViewItemMini from "./PinnedViewItemMini";

import css from "./PinnedViewsList.module.scss";

type Props = {
  pinnedViews: BasicView[];
  setSelectedView: (viewId?: string, viewName?: string, context?: string) => void;
};

function PinnedViewsList({ pinnedViews, setSelectedView }: Props) {
  const [views, setViews] = useState(pinnedViews.map((view) => ({ ...view, id: view.id || "" })));
  const { mutate: updateView } = useUpdatePinnedViewOld();
  const { mutate: updatePinnedView } = useUpdatePinnedView();
  const dataType = pinnedViews[0]?.dataType;

  useEffect(() => {
    setViews(pinnedViews.map((view) => ({ ...view, id: view.id || "" })));
  }, [pinnedViews]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over?.id && active.id !== over.id) {
      const newIndex: number = over.data.current?.sortable.index;
      const droppedView = views.find((view) => view.id === active.id);
      if (droppedView) {
        const updatedViews = views.filter((view) => view.id !== active.id);
        updatedViews.splice(newIndex, 0, droppedView);
        setViews(updatedViews);
        if (dataType === "notice") {
          updateView({ id: active.id.toString(), rank: newIndex, name: droppedView.name });
        } else {
          updatePinnedView({ input: { savedViewId: active.id.toString(), rank: newIndex + 1 } });
        }
      }
    }
  };

  return (
    <div className={css.savedViewsBar}>
      <ul className={css.quickSelectMenu}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={views} strategy={verticalListSortingStrategy}>
            {views.map((view) => (
              <PinnedViewItemMini key={view.id} view={view} setSelectedView={setSelectedView} />
            ))}
          </SortableContext>
        </DndContext>
      </ul>
    </div>
  );
}

export default PinnedViewsList;
